/**
 * Payroll API React Query Hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listPayrollPeriods, getPayrollPeriod, approvePayrollPeriod, approveEmployeeTimesheet } from '../lib/api/payroll.client';
/**
 * List payroll periods
 */
export function usePayrollPeriods(count) {
    return useQuery({
        queryKey: ['payroll', 'periods', count],
        queryFn: () => listPayrollPeriods(count),
        retry: (failureCount, error) => {
            // Don't retry on 404s (no periods exist yet) or 403s (unauthorized)
            if (error instanceof Error && (error.message.includes('404') || error.message.includes('403'))) {
                return false;
            }
            return failureCount < 3;
        },
    });
}
/**
 * Get payroll period details
 */
export function usePayrollPeriod(periodId) {
    return useQuery({
        queryKey: ['payroll', 'period', periodId],
        queryFn: () => getPayrollPeriod(periodId),
        enabled: !!periodId,
    });
}
/**
 * Approve payroll period mutation (admin only - approves entire period)
 */
export function useApprovePayrollPeriod() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ periodId, notes }) => approvePayrollPeriod(periodId, notes),
        onSuccess: (_data, variables) => {
            // Invalidate period list and specific period
            queryClient.invalidateQueries({ queryKey: ['payroll', 'periods'] });
            queryClient.invalidateQueries({ queryKey: ['payroll', 'period', variables.periodId] });
        },
    });
}
/**
 * Approve individual employee timesheet mutation (manager or admin)
 */
export function useApproveEmployeeTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ periodId, employeeId }) => approveEmployeeTimesheet(periodId, employeeId),
        onSuccess: (_data, variables) => {
            // Invalidate the specific period to refresh employee timesheet statuses
            queryClient.invalidateQueries({ queryKey: ['payroll', 'period', variables.periodId] });
            queryClient.invalidateQueries({ queryKey: ['payroll', 'periods'] });
        },
    });
}
